/**
 * Renders the triangular battlefield map with terrain features,
 * grid overlay, and color-coded player starting zones.
 */

import { Container, Graphics } from 'pixi.js';

const WORLD_W = 2400;
const WORLD_H = 1800;
const BG_COLOR = 0x0a0a1e;
const TERRAIN_COLOR = 0x1a3a2e;
const LIGHT_COLOR = 0x2a4a3e;
const CENTER_GLOW_COLOR = 0x446622;
const CENTER_RADIUS = 120;
const GRID_SIZE = 64;

const ZONE_COLORS: [number, number][] = [
  [0, 0],           // Player 0: blue, top-left
  [WORLD_W, 0],      // Player 1: red, top-right
  [WORLD_W / 2, WORLD_H], // Player 2: green, bottom-center
];

const PLAYER_ZONE_COLORS = [0x2244aa, 0xaa2222, 0x22aa44];

export class RenderMapTerrain {
  private container: Container;
  private gfx: Graphics;
  private gridGfx: Graphics;
  gridVisible = false;

  constructor(container: Container) {
    this.container = container;
    this.gfx = new Graphics();
    this.gridGfx = new Graphics();
    this.gridGfx.visible = false;
    this.container.addChild(this.gfx);
    this.container.addChild(this.gridGfx);
  }

  draw(): void {
    const g = this.gfx;
    g.clear();

    // Background
    g.rect(0, 0, WORLD_W, WORLD_H);
    g.fill(BG_COLOR);

    // Color-coded corner player zones
    for (let i = 0; i < ZONE_COLORS.length; i++) {
      const [cx, cy] = ZONE_COLORS[i];
      g.circle(cx, cy, 350);
      g.fill({ color: PLAYER_ZONE_COLORS[i], alpha: 0.25 });
      // Zone label circle
      g.circle(cx, cy, 120);
      g.fill({ color: PLAYER_ZONE_COLORS[i], alpha: 0.4 });
    }

    // Light terrain fill for remainder
    g.rect(0, 0, WORLD_W, WORLD_H);
    g.fill({ color: LIGHT_COLOR, alpha: 0.15 });

    // Central zone — 天道遗迹
    const centerX = WORLD_W / 2;
    const centerY = WORLD_H / 2;
    g.circle(centerX, centerY, CENTER_RADIUS);
    g.fill({ color: CENTER_GLOW_COLOR, alpha: 0.3 });

    // Glowing ring around center
    g.circle(centerX, centerY, CENTER_RADIUS + 20);
    g.stroke({ color: 0x88cc44, alpha: 0.4, width: 3 });
    g.circle(centerX, centerY, CENTER_RADIUS + 40);
    g.stroke({ color: 0x88cc44, alpha: 0.15, width: 2 });

    // Lines from center to each corner (ley lines)
    for (const [cx, cy] of ZONE_COLORS) {
      g.moveTo(centerX, centerY);
      g.lineTo(cx, cy);
      g.stroke({ color: 0x335522, alpha: 0.3, width: 1 });
    }

    this.drawGrid();
  }

  private drawGrid(): void {
    const g = this.gridGfx;
    g.clear();

    for (let x = 0; x <= WORLD_W; x += GRID_SIZE) {
      g.moveTo(x, 0);
      g.lineTo(x, WORLD_H);
      g.stroke({ color: 0x335588, alpha: 0.15, width: 1 });
    }
    for (let y = 0; y <= WORLD_H; y += GRID_SIZE) {
      g.moveTo(0, y);
      g.lineTo(WORLD_W, y);
      g.stroke({ color: 0x335588, alpha: 0.15, width: 1 });
    }
  }

  toggleGrid(): void {
    this.gridVisible = !this.gridVisible;
    this.gridGfx.visible = this.gridVisible;
  }
}
